/**
 * Client-side package validation that mirrors the backend's static data integrity checks.
 * The backend (PackageService.ValidatePackageFullAsync) is the authoritative gate;
 * this module provides instant UI feedback without a round-trip.
 */

import { getTenantTimeZone } from './dateFormat';

const PLACEHOLDER_EMAIL_PATTERN = /@pending\.local$/i;
const SIGNING_GROUP_EMAIL_PATTERN = /@signing-group\.local$/i;

/**
 * Returns true if the email is a template placeholder (e.g. placeholder-1@pending.local).
 * @param {string} email
 * @returns {boolean}
 */
export function isPlaceholderEmail(email) {
    return !!email && PLACEHOLDER_EMAIL_PATTERN.test(email);
}

/**
 * Returns true if the email is an auto-generated signing group placeholder.
 * @param {string} email
 * @returns {boolean}
 */
export function isSigningGroupEmail(email) {
    return !!email && SIGNING_GROUP_EMAIL_PATTERN.test(email);
}

/**
 * Basic email format check (mirrors backend IsValidEmailFormat).
 * @param {string} email
 * @returns {boolean}
 */
export function isValidEmail(email) {
    if (!email || !email.trim()) return false;
    const at = email.indexOf('@');
    if (at <= 0 || at >= email.length - 1) return false;
    const domain = email.slice(at + 1);
    return domain.includes('.') && !domain.endsWith('.');
}

/**
 * Validates a phone number has 10 digits (or 11 starting with 1).
 * Strips all non-digit formatting before checking.
 * @param {string} phone
 * @returns {boolean}
 */
export function isValidPhone(phone) {
    if (!phone) return false;
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits[0] === '1');
}

/**
 * Comprehensive client-side package validation.
 * Returns { isValid: boolean, errors: Array<{ code, field, signerId?, message }> }.
 *
 * @param {Object} state - Pinia store state with basicInfo, documents, signers, annotations
 * @returns {{ isValid: boolean, errors: Array<{ code: string, field: string, signerId?: string, message: string }> }}
 */
export function validatePackage(state) {
    const errors = [];

    // ── Package basics ──
    if (!state.basicInfo?.name?.trim()) {
        errors.push({ code: 'MISSING_NAME', field: 'name', message: 'Package name is required.' });
    }

    // ── Documents ──
    const docs = state.documents || [];
    if (docs.length === 0) {
        errors.push({ code: 'NO_DOCUMENTS', field: 'documents', message: 'At least one document is required.' });
    }

    const unready = docs.filter(d => d.processingStatus !== 'Ready');
    if (unready.length > 0) {
        errors.push({
            code: 'DOCUMENT_NOT_READY', field: 'documents',
            message: `${unready.length} document(s) are not ready for signing.`
        });
    }

    // ── Signers ──
    const signers = state.signers || [];
    if (signers.length === 0) {
        errors.push({ code: 'NO_SIGNERS', field: 'signers', message: 'At least one signer is required.' });
    }

    const activeTypes = ['Signer', 'Approver'];
    if (!signers.some(s => activeTypes.includes(s.type))) {
        errors.push({
            code: 'NO_ACTIVE_SIGNERS', field: 'signers',
            message: 'At least one active signer (Signer or Approver) is required.'
        });
    }

    // Types that must have at least one annotation to send (mirrors backend PackageService)
    const typesRequiringAnnotations = ['Signer', 'InPersonSigner'];
    const annotations = state.annotations || [];

    for (const signer of signers) {
        const label = signer.roleName || signer.name || '(unnamed)';
        const isGroupSigner = !!signer.signingGroupId;
        const hasFields = typesRequiringAnnotations.includes(signer.type)
            ? annotations.some(a => a.signerIndex === signer.routingOrder)
            : true; // non-signing types don't need annotations

        // Name (skip for group signers - auto-populated from group name)
        if (!isGroupSigner && !signer.name?.trim()) {
            errors.push({
                code: 'SIGNER_MISSING_NAME', field: 'signers', signerId: signer.id,
                message: `Signer "${label}" is missing a name.`
            });
        }

        // Email (skip for group signers - uses placeholder, members are notified via group)
        if (isGroupSigner) {
            // No email validation needed for group-based signers
        } else if (!signer.email?.trim()) {
            errors.push({
                code: 'SIGNER_MISSING_EMAIL', field: 'signers', signerId: signer.id,
                message: `Signer "${label}" is missing an email address.`
            });
        } else if (isPlaceholderEmail(signer.email)) {
            if (!hasFields) {
                // Signer has placeholder email AND no fields - they may not be needed at all
                errors.push({
                    code: 'SIGNER_PLACEHOLDER_NO_FIELDS', field: 'signers', signerId: signer.id,
                    message: `Signer "${label}" has a placeholder email and no fields assigned. Either update their email and add fields, or remove this signer.`
                });
            } else {
                errors.push({
                    code: 'SIGNER_PLACEHOLDER_EMAIL', field: 'signers', signerId: signer.id,
                    message: `Signer "${label}" still has a placeholder email and must be updated.`
                });
            }
        } else if (!isValidEmail(signer.email)) {
            errors.push({
                code: 'SIGNER_INVALID_EMAIL', field: 'signers', signerId: signer.id,
                message: `Signer "${label}" has an invalid email address.`
            });
        }

        // SMS auth → phone required and must be 10 digits
        const authMethod = signer.authMethod || '';
        if (authMethod === 'SMS' || authMethod === 'Sms') {
            const phone = signer.phone || signer.phoneForSms || '';
            if (!phone) {
                errors.push({
                    code: 'SIGNER_MISSING_PHONE', field: 'signers', signerId: signer.id,
                    message: `Signer "${label}" uses SMS authentication but has no phone number.`
                });
            } else if (!isValidPhone(phone)) {
                errors.push({
                    code: 'SIGNER_INVALID_PHONE', field: 'signers', signerId: signer.id,
                    message: `Signer "${label}" has an invalid phone number (must be 10 digits, or 11 starting with 1).`
                });
            }
        }

        // Signers/InPersonSigners must have at least one field assigned
        if (typesRequiringAnnotations.includes(signer.type) && !hasFields) {
            // Only add this if we didn't already flag it via SIGNER_PLACEHOLDER_NO_FIELDS
            if (!isPlaceholderEmail(signer.email)) {
                errors.push({
                    code: 'SIGNER_NO_ANNOTATIONS', field: 'annotations', signerId: signer.id,
                    message: `Signer "${label}" has no fields assigned. Add at least one field or remove this signer.`
                });
            }
        }
    }

    // ── Duplicate email+type ──
    const seen = new Map(); // key: "email|type" → first signer label
    for (const signer of signers) {
        if (!signer.email?.trim() || isPlaceholderEmail(signer.email) || isSigningGroupEmail(signer.email)) continue;
        const key = `${signer.email.trim().toLowerCase()}|${(signer.type || 'Signer')}`;
        if (seen.has(key)) {
            const label = signer.roleName || signer.name || '(unnamed)';
            errors.push({
                code: 'SIGNER_DUPLICATE_EMAIL_TYPE', field: 'signers', signerId: signer.id,
                message: `"${label}" has the same email and role as "${seen.get(key)}". Each participant should have a unique email per role.`
            });
        } else {
            seen.set(key, signer.roleName || signer.name || '(unnamed)');
        }
    }

    // ── Payment ──
    if (state.basicInfo?.requiresPayment) {
        if (!state.basicInfo.paymentAmount || state.basicInfo.paymentAmount <= 0) {
            errors.push({
                code: 'PAYMENT_NO_AMOUNT', field: 'payment',
                message: 'Payment is enabled but no valid amount is set.'
            });
        }
    }

    // ── Expiration ──
    // Compare in tenant timezone (or UTC if no tenant TZ set).
    // "Expires March 16" means valid through all of March 16 in the tenant's timezone.
    if (state.basicInfo?.expirationDate) {
        const expDate = state.basicInfo.expirationDate; // "YYYY-MM-DD" from date picker
        const tz = getTenantTimeZone();
        const today = new Date().toLocaleDateString('en-CA', {
            timeZone: tz || 'UTC'
        }); // "YYYY-MM-DD" in tenant timezone
        if (expDate < today) {
            errors.push({
                code: 'EXPIRATION_IN_PAST', field: 'expiration',
                message: 'Expiration date is in the past.'
            });
        }
    }

    return { isValid: errors.length === 0, errors };
}
